






























































import useFileDownloader from '@/composables/useFileDownloader';
import { useApolloQuery } from '@/gql/apolloWrapper';
import useCurrentUser from '@/hooks/useCurrentUser';

import { useGetUserCoursesQuery } from '@/gql/generated';
import mapCoursesToBundles from '@/modules/Training/utils/mapCoursesToBundles';
import useNotifications from '@bambeehr/use-notifications';
import {
  computed,
  defineComponent,
  ref,
  useRoute,
  useRouter,
} from '@nuxtjs/composition-api';
import EmployeeTrainingDetailHeader from './EmployeeTrainingDetailHeader.vue';
import EmployeeTrainingDetailCourses from './EmployeeTrainingDetailCourses.vue';
import { BaseBanner, BaseButton } from '@bambeehr/pollen';
import useTrainingCatalog from '@/modules/Training/hooks/useTrainingCatalog';
import useSelfUnenrollRetired from '@/modules/Training/hooks/useSelfUnenrollRetired';
import BreadCrumbs from '@/components/BreadCrumbs';
import ScormPlayer from '@/modules/Training/components/ScormPlayer';

export const getCourseDetailsLabel = (isBundle: boolean) =>
  `About this ${isBundle ? 'Program' : 'Course'}`;

export default defineComponent({
  components: {
    EmployeeTrainingDetailHeader,
    EmployeeTrainingDetailCourses,
    BreadCrumbs,
    BaseBanner,
    BaseButton,
    ScormPlayer,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { fileDownloader } = useFileDownloader();
    const { id } = route.value.params;
    const { currentUserId, isOwnerAdmin } = useCurrentUser();
    const { addSuccess } = useNotifications();
    const trainingItem = ref();
    const isLoading = ref(false);

    const isBundle = computed(() => trainingItem.value?.isBundle);
    const isTopicRetired = computed(() => trainingItem.value?.isTopicRetired ?? false);

    const { selfUnenroll, isPending: unenrollPending } = useSelfUnenrollRetired();

    const handleSelfUnenroll = () => {
      const topicId = trainingItem.value?.topicId;
      if (!topicId) return;
      selfUnenroll(topicId, () => {
        router.push(isOwnerAdmin.value ? '/training/my-courses' : '/my-training');
      });
    };

    const { availableBundles, availableCourses } = useTrainingCatalog();
    const coursesInfo = computed(() => {
      if (!trainingItem.value) {
        return [];
      }

      const contentId = isBundle.value
        ? trainingItem.value?.bundleId
        : trainingItem.value?.topicId;
      const topics = isBundle.value
        ? trainingItem.value?.topics
        : [trainingItem.value];

      const matchingContent = isBundle.value
        ? availableBundles.value.find((i) => i.id === contentId)
        : availableCourses.value.find((i) => i.id === contentId);

      return topics.map((topic) => ({
        id: topic.id,
        topicId: topic.topicId ?? topic.id,
        ticketId: topic.ticketId,
        name: topic.name ?? matchingContent?.name ?? '',
        startedAt: topic.startedAt,
        completedAt: topic.completedAt,
        certificateDownloadUrl: topic.certificateDownloadUrl,
        imageUrl: matchingContent?.imageUrl,
        description: matchingContent?.description ?? '',
      }));
    });

    const description = computed(() =>
      isBundle.value
        ? trainingItem.value.description
        : coursesInfo.value[0]?.description
    );

    const fetchUserCourses = () => {
      useApolloQuery(
        useGetUserCoursesQuery,
        { data: { userId: currentUserId.value } },
        {
          data: trainingItem,
          pending: isLoading,
        },
        {
          placeholderPick: ({ userCourses: res }) =>
            mapCoursesToBundles(res).find((i) => i.id === id),
        }
      );
    };

    fetchUserCourses();

    const courseDetailsLabel = computed(() =>
      getCourseDetailsLabel(isBundle.value)
    );

    const courseCount = computed(() =>
      isBundle.value ? trainingItem.value?.topics?.length ?? 0 : 0
    );

    const downloadCert = (item?) => {
      let certUrls: string[];
      if (isBundle.value) {
        certUrls = item
          ? [item.certificateDownloadUrl]
          : trainingItem.value?.certificateDownloadUrlList;
      } else {
        certUrls = [trainingItem.value?.certificateDownloadUrl];
      }
      certUrls.forEach((url) => {
        fileDownloader(url, '');
      });

      addSuccess(`Downloading. This may take a moment.`);
    };

    // For single courses, use the first course's ticketId.
    // For bundles, read ?ticketId= query param (set when clicking a course card).
    const activeTicketId = computed(() => {
      if (!isBundle.value) {
        return coursesInfo.value[0]?.ticketId ?? null;
      }
      const qTicket = route.value.query.ticketId as string | undefined;
      return qTicket ?? null;
    });

    const playCourse = (topicId: string) => {
      const course = coursesInfo.value.find((c) => c.topicId === topicId);
      if (course?.ticketId) {
        router.push({ path: route.value.path, query: { ticketId: course.ticketId } });
      }
    };

    const onCourseCompleted = () => {
      if (isBundle.value) {
        // Navigate back to bundle view (remove query param)
        router.push({ path: route.value.path });
      }
      addSuccess('Course completed! Your certificate will be available shortly.');
      fetchUserCourses();
    };

    const crumbs = computed(() =>
      [
        isOwnerAdmin.value && {
          label: 'Training',
          to: '/training/overview',
        },
        {
          label: 'My Training',
          to: isOwnerAdmin.value ? '/training/my-courses' : '/my-training',
        },
        {
          label: trainingItem.value?.name || '',
        },
      ].filter(Boolean)
    );

    return {
      crumbs,
      trainingItem,
      courseDetailsLabel,
      isBundle,
      courseCount,
      downloadCert,
      coursesInfo,
      description,
      activeTicketId,
      playCourse,
      onCourseCompleted,
      isTopicRetired,
      unenrollPending,
      handleSelfUnenroll,
    };
  },
});
